import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Plus, ChevronLeft, ChevronRight,
  FileText, Loader2, AlertCircle, X,
  FileSpreadsheet, Download
} from 'lucide-react';
import axios from '../axiosInstance';

/* ───────────── helpers ───────────── */
const formatCurrency = (val) => {
  if (val === null || val === undefined || val === '') return '—';
  return `₹${parseFloat(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/* ───────────── component ───────────── */
export default function SlaList() {
  const navigate = useNavigate();

  /* state */
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [undertakingLoading, setUndertakingLoading] = useState(null);
  const [tableLoading, setTableLoading] = useState(null);

  const fetchRef = useRef(null);

  /* ── fetch list ── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);

      const res = await axios.get(`sla?${params.toString()}`);
      const d = res.data;
      const records = d.data || d.slas || (Array.isArray(d) ? d : []);
      setData(records);
      const t = d.total || d.meta?.total || records.length;
      setTotal(t);
      setTotalPages(Math.ceil(t / limit) || 1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch SLA records');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    if (fetchRef.current) clearTimeout(fetchRef.current);
    fetchRef.current = setTimeout(fetchData, 200);
    return () => { if (fetchRef.current) clearTimeout(fetchRef.current); };
  }, [fetchData]);

  /* ── download undertaking PDF ── */
  const handleUndertaking = async (slaId) => {
    setUndertakingLoading(slaId);
    try {
      const res = await axios.get(`sla/${slaId}/undertaking/pdf`);
      const { document_url } = res.data;
      if (document_url) {
        window.open(document_url, '_blank');
      } else {
        setError('No document URL returned for undertaking PDF');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch undertaking PDF');
    } finally {
      setUndertakingLoading(null);
    }
  };

  /* ── download table PDF ── */
  const handleTable = async (slaId) => {
    setTableLoading(slaId);
    try {
      const res = await axios.get(`sla/${slaId}/table/pdf`);
      const { document_url } = res.data;
      if (document_url) {
        window.open(document_url, '_blank');
      } else {
        setError('No document URL returned for table PDF');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch table PDF');
    } finally {
      setTableLoading(null);
    }
  };

  /* ── render ── */
  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 text-slate-800 font-sans">
      <div className="w-full">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4"
        >
          <div>
            <h5 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Incidental Management</h5>
            <h1 className="text-3xl md:text-4xl font-black text-blue-800 tracking-tight">SLA Records</h1>
            <p className="text-slate-500 mt-1 text-sm">Manage SLA entries with MSP calculations.</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate('/incidental/sla/create')}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-2xl font-bold shadow-lg shadow-blue-100 transition-all text-sm"
          >
            <Plus size={20} /> Create SLA
          </motion.button>
        </motion.div>



        {/* ── Error ── */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mb-6 flex items-center gap-3 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm font-semibold">
              <AlertCircle size={20} /> {error}
              <button onClick={() => setError('')} className="ml-auto p-1 hover:bg-red-100 rounded-lg transition"><X size={16} /></button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Table ── */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[1100px]">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  {['#', 'Season', 'Commodity', 'Bags', 'Quantity (Qtl)', 'MSP (₹/Qtl)', 'Amount (₹)', 'Undertaking', 'Table'].map(h => (
                    <th key={h} className="px-5 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-3 text-blue-600">
                        <Loader2 size={32} className="animate-spin" />
                        <span className="text-sm font-bold">Loading records...</span>
                      </div>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-3 text-slate-400">
                        <FileText size={40} strokeWidth={1.5} />
                        <span className="text-sm font-bold">No SLA records found</span>
                        <p className="text-xs text-slate-400 max-w-xs">Create a new SLA entry to get started.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((item, idx) => (
                    <motion.tr
                      key={item.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.03 }}
                      className="hover:bg-blue-50/40 transition-colors group"
                    >
                      <td className="px-5 py-4 text-xs font-mono text-slate-400">{(page - 1) * limit + idx + 1}</td>
                      <td className="px-5 py-4 text-sm font-semibold text-slate-800">{item.season?.name || '—'}</td>
                      <td className="px-5 py-4 text-sm font-medium text-slate-700">{item.commodity?.commodityname || item.commodity?.name || '—'}</td>
                      <td className="px-5 py-4 text-sm font-bold text-slate-800">{item.bags ?? '—'}</td>
                      <td className="px-5 py-4 text-sm font-bold text-slate-800">{item.quantity ? parseFloat(item.quantity).toLocaleString('en-IN') : '—'}</td>
                      <td className="px-5 py-4 text-sm font-bold text-indigo-700">{item.msp ? formatCurrency(item.msp) : '—'}</td>
                      <td className="px-5 py-4 text-sm font-bold text-emerald-700">{item.amount ? formatCurrency(item.amount) : '—'}</td>

                      {/* Undertaking HTML */}
                      <td className="px-5 py-4 text-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleUndertaking(item.id); }}
                          disabled={undertakingLoading === item.id}
                          title="View Undertaking"
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all disabled:opacity-50"
                        >
                          {undertakingLoading === item.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        </button>
                      </td>
                      {/* Table HTML */}
                      <td className="px-5 py-4 text-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleTable(item.id); }}
                          disabled={tableLoading === item.id}
                          title="View Table"
                          className="p-2 text-green-400 hover:text-green-600 hover:bg-green-50 rounded-xl transition-all disabled:opacity-50"
                        >
                          {tableLoading === item.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        </button>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data.length > 0 && (
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 p-5 border-t border-slate-100 bg-slate-50/30">
              <div className="text-xs text-slate-500 font-medium">
                Showing <span className="text-blue-600 font-bold">{Math.min(total, (page - 1) * limit + 1)}</span> to{' '}
                <span className="text-blue-600 font-bold">{Math.min(total, page * limit)}</span> of{' '}
                <span className="text-blue-600 font-bold">{total}</span> records
              </div>
              <div className="flex items-center gap-2">
                <button disabled={page === 1 || loading} onClick={() => setPage(p => p - 1)}
                  className="flex items-center gap-1 px-4 py-2 bg-white border border-slate-200 hover:border-blue-300 text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition font-bold text-xs">
                  <ChevronLeft size={16} /> Prev
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pn;
                    if (totalPages <= 5) pn = i + 1;
                    else if (page <= 3) pn = i + 1;
                    else if (page >= totalPages - 2) pn = totalPages - 4 + i;
                    else pn = page - 2 + i;
                    return (
                      <button key={pn} onClick={() => setPage(pn)}
                        className={`w-9 h-9 flex items-center justify-center rounded-xl font-bold text-xs transition ${page === pn ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                          }`}>{pn}</button>
                    );
                  })}
                </div>
                <button disabled={page === totalPages || loading} onClick={() => setPage(p => p + 1)}
                  className="flex items-center gap-1 px-4 py-2 bg-white border border-slate-200 hover:border-blue-300 text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition font-bold text-xs">
                  Next <ChevronRight size={16} />
                </button>
              </div>
              <div className="hidden md:block text-xs font-semibold text-slate-400">
                Page <span className="text-blue-600">{page}</span> of {totalPages}
              </div>
            </div>
          )}
        </motion.div>


      </div>
    </div>
  );
}
